import React from 'react';

interface HtmlDescriptionProps {
  html?: string | null;
  className?: string;
}

const droppedTags = new Set(['script', 'style', 'iframe', 'object', 'embed', 'svg']);

const renderNode = (node: Node, key: React.Key): React.ReactNode => {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent;
  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const element = node as HTMLElement;
  const tag = element.tagName.toLowerCase();
  if (droppedTags.has(tag)) return null;

  const children = Array.from(element.childNodes).map((child, index) => renderNode(child, index));

  switch (tag) {
    case 'p':
      return <p key={key} className="[&:not(:first-child)]:mt-3">{children}</p>;
    case 'ul':
      return <ul key={key} className="my-3 list-disc space-y-1 pl-5">{children}</ul>;
    case 'ol':
      return <ol key={key} className="my-3 list-decimal space-y-1 pl-5">{children}</ol>;
    case 'li':
      return <li key={key}>{children}</li>;
    case 'br':
      return <br key={key} />;
    case 'strong':
    case 'b':
      return <strong key={key} className="font-semibold text-foreground">{children}</strong>;
    case 'em':
    case 'i':
      return <em key={key}>{children}</em>;
    default:
      // Unknown tags are unwrapped. Their attributes are never copied to React,
      // so event handlers, styles and unsafe links cannot reach the DOM.
      return <React.Fragment key={key}>{children}</React.Fragment>;
  }
};

/** Safely renders the small HTML subset supported by product descriptions. */
const HtmlDescription: React.FC<HtmlDescriptionProps> = ({ html, className = '' }) => {
  if (!html) return null;

  if (typeof DOMParser === 'undefined') {
    return <div className={`whitespace-pre-line ${className}`}>{html}</div>;
  }

  const document = new DOMParser().parseFromString(html, 'text/html');
  const content = Array.from(document.body.childNodes).map((node, index) => renderNode(node, index));

  return <div className={`whitespace-pre-line ${className}`}>{content}</div>;
};

export default HtmlDescription;
